import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import CustomInput from "../Forms/CustomInput";
import CustomSelect from "./CustomSelect";
import CustomTextarea from "../Forms/CustomTextarea";
import { getAssetImportantLevelOptions } from "@constants/policy";
import { CHARACTER_LENGTH } from "@constants/index";
import useEscapeKey from "@hooks/useEscapeKey";
import { OutlineButton, PrimaryButton } from "@components/Buttons";

const AddAssetClassificationModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  "data-testid": dataTestId = "add-asset-classification-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const fieldErrorTestId = (name) => `${dataTestId}-field-${name}-error`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  
  const [formData, setFormData] = useState({
    assetClassificationName: "",
    importanceLevelCd: "",
    remark: "",
  });

  const [errors, setErrors] = useState({});

  const assetImportantLevelOptions = getAssetImportantLevelOptions(t);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        assetClassificationName: "",
        importanceLevelCd: "",
        remark: "",
      });
      setErrors({});
    }
  }, [isOpen]);

  useEscapeKey(onClose, isOpen && !loading);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.assetClassificationName.trim()) {
      newErrors.assetClassificationName = t("policy.asset-classification-name-required");
    }
    
    if (!formData.importanceLevelCd) {
      newErrors.importanceLevelCd = t("policy.asset-importance-level-required");
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = formData.assetClassificationName.trim() !== "" && formData.importanceLevelCd !== "";

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-start sm:justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px] overflow-x-auto p-4`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`relative min-w-[508px] flex flex-col w-full max-w-lg ${colors.bgModal} transform overflow-hidden rounded-lg shadow-xl transition-all duration-200 border ${colors.borderPrimary}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={dataTestId}
      >
        {/* Header */}
        <div className={`flex items-center justify-between border-b p-6 ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <h3 className={`text-lg font-semibold ${colors.textPrimary}`} data-testid={`${dataTestId}-title`}>
            {t("policy.add-asset-classification-modal-title")}
          </h3>
        </div>

        {/* Content */}
        <div className="p-6" data-testid={`${dataTestId}-content`}>
          {/* Description */}
          <p className={`mb-6 text-sm ${colors.textSecondary}`} data-testid={`${dataTestId}-description`}>
            {t("policy.add-asset-classification-modal-description")}
          </p>

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Asset Classification Name */}
            <div data-testid={fieldContainerTestId("asset-classification-name")}>
              <label className={`mb-2 block text-sm font-semibold ${colors.textPrimary}`} data-testid={fieldLabelTestId("asset-classification-name")}>
                {t("policy.asset-classification-name-label")} <span className={colors.textDanger}>*</span>
              </label>
              <CustomInput
                type="text"
                value={formData.assetClassificationName}
                onChange={(e) => handleInputChange("assetClassificationName", e.target.value)}
                placeholder={t("policy.enter-asset-classification-name")}
                className="w-full"
                size="md"
                maxLength={CHARACTER_LENGTH.COMMENT}
                data-testid={fieldInputTestId("asset-classification-name")}
              />
              {errors.assetClassificationName && (
                <p className={`mt-1 text-xs ${colors.textDanger}`} data-testid={fieldErrorTestId("asset-classification-name")}>{errors.assetClassificationName}</p>
              )}
            </div>

            {/* Asset Importance Level */}
            <div data-testid={fieldContainerTestId("importance-level")}>
              <label className={`mb-2 block text-sm font-semibold ${colors.textPrimary}`} data-testid={fieldLabelTestId("importance-level")}>
                {t("policy.asset-importance-level-label")} <span className={colors.textDanger}>*</span>
              </label>
              <CustomSelect
                options={assetImportantLevelOptions}
                value={assetImportantLevelOptions.find(
                  (opt) => opt.value === formData.importanceLevelCd
                ) || null}
                onChange={(opt) => handleInputChange("importanceLevelCd", opt?.value || "")}
                placeholder={t("policy.select-level")}
                className="w-full"
                size="md"
                data-testid={fieldInputTestId("importance-level")}
              />
              {errors.importanceLevelCd && (
                <p className={`mt-1 text-xs ${colors.textDanger}`} data-testid={fieldErrorTestId("importance-level")}>{errors.importanceLevelCd}</p>
              )}
            </div>

            {/* Remark */}
            <div data-testid={fieldContainerTestId("remark")}>
              <label className={`mb-2 block text-sm font-semibold ${colors.textPrimary}`} data-testid={fieldLabelTestId("remark")}>
                {t("common.remark")}
              </label>
              <CustomTextarea
                value={formData.remark}
                onChange={(e) => handleInputChange("remark", e.target.value)}
                placeholder={t("policy.write-remark")}
                className="w-full"
                size="md"
                rows={4}
                maxLength={CHARACTER_LENGTH.REMARK}
                data-testid={fieldInputTestId("remark")}
              />
              <p className={`mt-1 text-xs ${colors.textCharacterCount}`} data-testid="add-asset-classification-modal-remark-char-count">{(formData.remark || "").length}/{CHARACTER_LENGTH.REMARK}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-8" data-testid={`${dataTestId}-footer`}>
            <OutlineButton
              data-testid={`${dataTestId}-cancel-button`}
              onClick={onClose}
              disabled={loading}
            >
              {t("common.cancel-button")}
            </OutlineButton>
            <PrimaryButton
              data-testid={`${dataTestId}-confirm-button`}
              onClick={handleSubmit}
              disabled={loading || !isFormValid}
            >
              {loading ? t("common.saving") : t("common.save-button")}
            </PrimaryButton>
          </div>
      </div>
      </div>
    </div>
  );
};

export default AddAssetClassificationModal;
